import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AccountAnalytics,
  Analytics as AnalyticsState,
  AnalyticsType,
  LogsAnalytics,
} from '@/shared/types/analytics';
import { formatDate } from '@/util/dateUtils';

export interface PeriodProps {
  startDate: string | Date;
  endDate: string | Date;
}

export interface ApplyFiltersPayload {
  filters: AccountAnalytics[] | LogsAnalytics[];
  type: AnalyticsType;
}

const getDefaultDate = () => formatDate(new Date());

export const defaultState: AnalyticsState = {
  startDate: getDefaultDate(),
  endDate: getDefaultDate(),
  lastSeen: [],
  logs: [],
  accountAnalytics: [],
  filtersLoaded: false,
  logsFiltersPanel: {
    openFilter: null,
    openFilterIndex: null,
    dropdownOpen: false,
    openSource: null,
  },
};
const resetAllFilters = (state: AnalyticsState) => {
  state.startDate = defaultState.startDate;
  state.endDate = defaultState.endDate;
  state.logs = defaultState.logs;
  state.accountAnalytics = defaultState.accountAnalytics;
  state.lastSeen = defaultState.lastSeen;
  state.logsFiltersPanel = { ...defaultState.logsFiltersPanel };
};
const slice = createSlice({
  name: 'analytics',
  initialState: defaultState,
  reducers: {
    setPeriod: (state, action: PayloadAction<PeriodProps>) => {
      const { startDate, endDate } = action.payload ?? {};

      if (startDate instanceof Date) {
        state.startDate = startDate.toISOString();
      } else {
        state.startDate = startDate;
      }
      if (endDate instanceof Date) {
        state.endDate = endDate.toISOString();
      } else {
        state.endDate = endDate;
      }
    },

    applyFilters: (state, action: PayloadAction<ApplyFiltersPayload>) => {
      const filters = action.payload.filters;
      const type = action.payload.type;

      if (type === AnalyticsType.LOGS) {
        state.logs = filters as AnalyticsState['logs'];
      } else if (type === AnalyticsType.ACCOUNT) {
        state.accountAnalytics = filters as AccountAnalytics[];
      }
      state.filtersLoaded = true;
    },

    addLastSeen: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (state.lastSeen.includes(sessionId)) {
        state.lastSeen.push(state.lastSeen.splice(state.lastSeen.indexOf(sessionId), 1)[0]);
      } else {
        state.lastSeen.push(sessionId);
      }
    },

    resetFilters: (state) => {
      resetAllFilters(state);
    },
    openLogsFilterPanel: (
      state,
      action: PayloadAction<{ filterKey: string; filterIndex: number; source?: 'menu' | 'pill' }>
    ) => {
      state.logsFiltersPanel.openFilter = action.payload.filterKey;
      state.logsFiltersPanel.openFilterIndex = action.payload.filterIndex;
      state.logsFiltersPanel.dropdownOpen = false;
      state.logsFiltersPanel.openSource = action.payload.source ?? 'menu';
    },
    closeLogsFilterPanel: (state) => {
      state.logsFiltersPanel.openFilter = null;
      state.logsFiltersPanel.openFilterIndex = null;
      state.logsFiltersPanel.openSource = null;
    },
    setLogsFiltersDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.logsFiltersPanel.dropdownOpen = action.payload;
    },
    setLogsFilterPanelIndex: (state, action: PayloadAction<number | null>) => {
      state.logsFiltersPanel.openFilterIndex = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('session/setAccountSlug', (state) => {
      resetAllFilters(state);
    });
  },
});

// Extract and export each action creator by name
export const {
  setPeriod,
  applyFilters,
  addLastSeen,
  resetFilters,
  openLogsFilterPanel,
  closeLogsFilterPanel,
  setLogsFiltersDropdownOpen,
  setLogsFilterPanelIndex,
} = slice.actions;

// Export the reducer, either as a default or named export
export default slice.reducer;
